import React from 'react';
import { DateTime } from 'luxon';
import { Badge, eFilterType, FilteredDataGrid, Currency } from 'ui-toolkit';
import { IPageData } from 'types/rest/IPageData';
import { ITableChangeState } from 'react-bootstrap-table-next';
import styled from 'styled-components';
import { IUnrestrictedInternalTransferData } from '../../types/accounting/data/IUnrestrictedInternalTransferData';
import { IUnrestrictedInternalTransfersQuery } from '../../types/accounting/query/IUnrestrictedInternalTransfersQuery';
import { cleanDataGridNames, decleanDataGridNames } from './cleanDataGridNames';
import { IFetchStatus } from 'types/rest/IFetchStatus';
import { useHasFeatureFlag } from '../../selectors/useHasFeatureFlag';

const ProcessingStatus = styled.div`
  strong {
    display: block;
  }
`;

export interface IUnrestrictedInternalTransfersSearchTableProps {
  data: IPageData<IUnrestrictedInternalTransferData>;
  fetchStatus: IFetchStatus;
  query: IUnrestrictedInternalTransfersQuery;
  setQuery: (query: IUnrestrictedInternalTransfersQuery) => void;
}

export const UnrestrictedInternalTransfersSearchTable: React.FC<IUnrestrictedInternalTransfersSearchTableProps> = ({
  data,
  fetchStatus,
  query,
  setQuery,
}) => {
  const isZillowClosingEnabled = useHasFeatureFlag('pinecone.zillow-closing');

  return (
    <>
      <FilteredDataGrid
        data-test-id="internal-transfers-search-table"
        columns={[
          {
            dataField: 'createdAt',
            filterDefault: query.createdAt,
            filterElement: eFilterType.DateRange,
            filterPlaceholder: 'Created At',
            formatter: (column: any) => DateTime.fromISO(column as string).toFormat('D'),
            headerStyle: { width: '130px' },
            text: 'Created At',
            sort: true,
          },
          {
            dataField: isZillowClosingEnabled ? 'sourceAccount.nickname' : 'sourceAccount.spruceNickname',
            filterDefault: query.sourceAccount,
            filterElement: eFilterType.Text,
            filterPlaceholder: 'Source Account',
            headerStyle: { width: '200px' },
            text: 'Source Account',
            sort: true,
          },
          {
            dataField: isZillowClosingEnabled ? 'destinationAccount.nickname' : 'destinationAccount.spruceNickname',
            filterDefault: query.destinationAccount,
            filterElement: eFilterType.Text,
            filterPlaceholder: 'Destination Account',
            headerStyle: { width: '200px' },
            text: 'Destination Account',
            sort: true,
          },
          {
            dataField: 'memo',
            filterDefault: query.memo,
            filterElement: eFilterType.Text,
            filterPlaceholder: 'Note',
            headerStyle: { width: '200px' },
            text: 'Note',
            sort: true,
          },
          {
            dataField: 'escrowFile.order.fileNumber',
            filterDefault: query.fileNumber,
            filterElement: eFilterType.Text,
            filterPlaceholder: 'Order Number',
            headerStyle: { width: '150px' },
            text: 'Order Number',
            sort: true,
          },
          {
            dataField: 'amountCents',
            filterDefault: typeof query.amountCents === 'number' ? query.amountCents.toString() : undefined,
            filterElement: eFilterType.Currency,
            filterPlaceholder: 'Amount',
            formatter: (value) => typeof value === 'number' && <Currency value={value} valueIsCents={true} />,
            headerStyle: { width: '120px' },
            text: 'Amount',
            sort: true,
          },
          {
            dataField: 'processingForDisbursementCompletedAt',
            formatter: (sentAt, value) => (
              <ProcessingStatus>
                <Badge intent={sentAt ? 'primary' : 'danger'}>
                  {sentAt ? `sent ${DateTime.fromISO(sentAt as string).toFormat('D')}` : 'uploaded'}
                </Badge>
              </ProcessingStatus>
            ),
            headerStyle: { width: '100px' },
            text: 'Status',
            sort: true,
          },
        ]}
        data={data.content}
        keyField="id"
        loading={fetchStatus.fetching}
        onTableChange={(type: string, tableState: ITableChangeState<IUnrestrictedInternalTransferData>) => {
          const newFilters = Object.keys(tableState.filters).reduce(
            (p, key) => ({ ...p, [cleanDataGridNames(key)]: tableState.filters[key].filterVal }),
            {}
          );
          setQuery({
            ...query,
            ...newFilters,
            pageNumber: tableState.page,
            pageSize: tableState.sizePerPage,
            sortField: tableState.sortField || undefined,
            sortOrder: tableState.sortOrder || undefined,
          });
        }}
        remote={true}
        pagination={{
          page: query.pageNumber || 1,
          sizePerPage: query.pageSize || 10,
          totalSize: data.totalElements,
        }}
        sort={{ dataField: decleanDataGridNames(query.sortField) || 'UNSET', order: query.sortOrder || 'asc' }}
      />
    </>
  );
};
